import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Download, Flame, Sparkles, Home, Lock, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";

const STATUSES = [
  { key: "NEW", label: "New", color: "border-blue-300 bg-blue-50/40", dot: "bg-blue-500" },
  { key: "CONTACTED", label: "Contacted", color: "border-amber-300 bg-amber-50/40", dot: "bg-amber-500" },
  { key: "SCHEDULED", label: "Scheduled", color: "border-violet-300 bg-violet-50/40", dot: "bg-violet-500" },
  { key: "WON", label: "Won", color: "border-emerald-300 bg-emerald-50/40", dot: "bg-emerald-500" },
  { key: "LOST", label: "Lost", color: "border-slate-300 bg-slate-50", dot: "bg-slate-400" },
];

export default function Leads() {
  const nav = useNavigate();
  const [leads, setLeads] = useState([]);
  const [company, setCompany] = useState(null);
  const [unlockSummary, setUnlockSummary] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/leads"),
      api.get("/companies/me"),
      api.get("/leads/unlock-summary").catch(() => ({ data: null })),
    ]).then(([l, c, s]) => {
      setLeads(l.data);
      setCompany(c.data);
      setUnlockSummary(s.data);
    });
  }, []);

  const grouped = STATUSES.reduce((acc, s) => {
    acc[s.key] = leads.filter((l) => l.status === s.key);
    return acc;
  }, {});

  const onDragEnd = async (result) => {
    if (!result.destination) return;
    const { source, destination, draggableId } = result;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;
    const newStatus = destination.droppableId;
    const oldLead = leads.find((l) => l.id === draggableId);
    if (!oldLead) return;
    // Optimistic update
    setLeads((prev) => prev.map((l) => (l.id === draggableId ? { ...l, status: newStatus } : l)));
    try {
      await api.patch(`/leads/${draggableId}`, { status: newStatus });
      toast.success(`Moved to ${newStatus}`);
    } catch {
      // revert
      setLeads((prev) => prev.map((l) => (l.id === draggableId ? oldLead : l)));
      toast.error("Couldn't update status");
    }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/leads/export.csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const link = document.createElement("a");
      link.href = url;
      const today = new Date().toISOString().slice(0, 10);
      link.download = `leads-${today}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch {
      toast.error("Couldn't export. Please try again.");
    }
  };

  return (
    <AppShell title="Quotes" subtitle="Drag cards between columns to update status" company={company}>
      {/* Free-tier unlock banner — only shown to free_claimed companies with locked leads */}
      {unlockSummary?.is_free_tier && unlockSummary.locked_count > 0 && (
        <div
          data-testid="free-tier-unlock-banner"
          className="mb-4 rounded-2xl border border-[#C75D33]/30 bg-gradient-to-r from-[#FFF8F1] to-[#FFE8D6] p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-4"
        >
          <div className="w-10 h-10 shrink-0 rounded-xl bg-[#C75D33] text-white flex items-center justify-center">
            <Lock className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold text-slate-900 text-base sm:text-lg leading-tight">
              You have <span className="text-[#C75D33]">{unlockSummary.locked_count} locked lead{unlockSummary.locked_count === 1 ? "" : "s"}</span>
              {unlockSummary.total_value_cents > 0 && (
                <> · <span className="text-slate-700">${(unlockSummary.total_value_cents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })} in customer quotes</span></>
              )}
            </div>
            <div className="text-xs sm:text-sm text-slate-600 mt-0.5">
              These are real homeowners who quoted through <strong>your</strong> funnel. Unlock their contact info with a $1 trial — $97/mo after 30 days. Cancel anytime.
            </div>
          </div>
          <Button
            onClick={() => nav("/billing")}
            data-testid="free-tier-unlock-cta"
            className="rounded-xl bg-[#C75D33] hover:bg-[#A84C28] text-white h-11 px-5 shrink-0"
          >
            <Zap className="w-4 h-4 mr-1.5" /> Unlock now — $1 trial
          </Button>
        </div>
      )}
      <div className="flex justify-end mb-4 -mt-2">
        <Button onClick={exportCsv} variant="outline" className="rounded-xl h-10" data-testid="leads-export-csv">
          <Download className="w-4 h-4 mr-1.5" /> Export CSV
        </Button>
      </div>
      <DragDropContext onDragEnd={onDragEnd}>
        <div className="flex gap-5 overflow-x-auto pb-4 scrollbar-thin -mx-4 px-4">
          {STATUSES.map((s) => (
            <Droppable key={s.key} droppableId={s.key}>
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  data-testid={`column-${s.key}`}
                  className={`w-80 shrink-0 rounded-2xl border-2 ${s.color} ${snapshot.isDraggingOver ? "ring-2 ring-[#C75D33]/40" : ""} p-3`}
                >
                  <div className="flex items-center justify-between px-2 py-2 mb-2">
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${s.dot}`}></span>
                      <span className="text-sm font-bold text-slate-900">{s.label}</span>
                    </div>
                    <span className="text-xs font-semibold text-slate-600 bg-white px-2 py-0.5 rounded-full">{grouped[s.key].length}</span>
                  </div>

                  <div className="space-y-2 min-h-[40px]">
                    {grouped[s.key].map((lead, i) => (
                      <Draggable key={lead.id} draggableId={lead.id} index={i}>
                        {(prov, snap) => (
                          <div
                            ref={prov.innerRef}
                            {...prov.draggableProps}
                            {...prov.dragHandleProps}
                            data-testid={`lead-card-${lead.id}`}
                            className={`bg-white rounded-xl border ${lead.ready_to_book ? "border-amber-300 ring-2 ring-amber-100" : "border-slate-200"} shadow-sm cursor-grab active:cursor-grabbing transition-shadow ${snap.isDragging ? "shadow-lg ring-2 ring-[#C75D33]/30" : ""} overflow-hidden`}
                          >
                            {/* Source ribbon — marketplace vs. own traffic */}
                            {lead.marketplace?.assignment_id ? (
                              <div className="flex items-center justify-between gap-2 px-3 py-1.5 bg-gradient-to-r from-[#0F172A] to-[#1E293B] text-white" data-testid={`lead-source-marketplace-${lead.id}`}>
                                <span className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-widest">
                                  <Sparkles className="w-3 h-3 text-[#E2A878]" /> Marketplace lead
                                </span>
                                <span className="text-[10px] font-bold text-[#E2A878]">
                                  ${(lead.marketplace.charge_cents / 100).toFixed(2)}
                                </span>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1 px-3 py-1.5 bg-slate-50 text-slate-500" data-testid={`lead-source-own-${lead.id}`}>
                                <Home className="w-3 h-3" />
                                <span className="text-[10px] font-extrabold uppercase tracking-widest">Your traffic · Free</span>
                              </div>
                            )}
                            <Link to={`/leads/${lead.id}`} className="block p-3.5">
                              {lead.ready_to_book && (
                                <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 border border-amber-300 text-amber-800 text-[10px] font-bold uppercase tracking-wider mb-2" data-testid={`lead-hot-${lead.id}`}>
                                  <Flame className="w-3 h-3" /> Ready to book
                                </div>
                              )}
                              <div className="flex items-start justify-between mb-2">
                                <div className="font-semibold text-slate-900 text-sm flex items-center gap-1.5">
                                  {lead.is_locked && <Lock className="w-3.5 h-3.5 text-[#C75D33]" data-testid={`lead-lock-icon-${lead.id}`} />}
                                  {lead.first_name} {lead.last_name}
                                </div>
                                <div className="text-sm font-bold text-[#C75D33]">${Number(lead.estimate_total).toLocaleString()}</div>
                              </div>
                              {lead.is_locked ? (
                                <div data-testid={`locked-lead-cta-${lead.id}`} className="rounded-lg border border-[#C75D33]/25 bg-[#FFF8F1] p-2.5">
                                  <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-[#C75D33] mb-1">
                                    <Lock className="w-3 h-3" /> Contact info locked
                                  </div>
                                  <div className="text-[11px] text-slate-600 leading-snug">
                                    {lead.zip_code || "—"} · {lead.services?.[0]?.quantity || 0} screens · quoted <strong className="text-slate-900">${Number(lead.estimate_total).toLocaleString()}</strong>
                                  </div>
                                  <button
                                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); nav("/billing"); }}
                                    data-testid={`locked-lead-unlock-btn-${lead.id}`}
                                    className="mt-1.5 w-full rounded-md bg-[#C75D33] hover:bg-[#A84C28] text-white text-[11px] font-bold py-1.5"
                                  >
                                    Unlock — $1 trial →
                                  </button>
                                </div>
                              ) : (
                                <div className="space-y-0.5 text-xs text-slate-500">
                                  <div className="flex items-center gap-1.5"><Phone className="w-3 h-3" /> {lead.phone}</div>
                                  <div className="flex items-center gap-1.5 truncate"><Mail className="w-3 h-3 shrink-0" /> <span className="truncate">{lead.email}</span></div>
                                  <div className="flex items-center gap-1.5"><MapPin className="w-3 h-3" /> {lead.zip_code}</div>
                                </div>
                              )}
                              <div className="mt-2 pt-2 border-t border-slate-100 text-[10px] text-slate-400 uppercase tracking-wider font-bold">
                                {new Date(lead.created_at).toLocaleDateString()} · {lead.services?.[0]?.quantity || 0} screens
                              </div>
                            </Link>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                </div>
              )}
            </Droppable>
          ))}
        </div>
      </DragDropContext>
    </AppShell>
  );
}
